import React, { useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { FaCheck, FaAward, FaUsers, FaTools, FaIndustry } from 'react-icons/fa';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './About.css';

const About: React.FC = () => {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      offset: 100
    });
  }, []);

  const stats = [
    { icon: <FaAward />, number: '25+', label: 'Anos de Experiência' },
    { icon: <FaUsers />, number: '500+', label: 'Clientes Atendidos' },
    { icon: <FaTools />, number: '1000+', label: 'Projetos Realizados' },
    { icon: <FaIndustry />, number: '50+', label: 'Estados Atendidos' }
  ];

  const values = [
    {
      title: 'Qualidade',
      description: 'Compromisso com a excelência em todos os nossos serviços e produtos.',
      icon: <FaAward />
    },
    {
      title: 'Inovação',
      description: 'Sempre buscando as melhores soluções e tecnologias para nossos clientes.',
      icon: <FaTools />
    },
    {
      title: 'Confiabilidade',
      description: 'Mais de 25 anos de experiência no mercado industrial.',
      icon: <FaIndustry />
    }
  ];

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="page-hero">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8} data-aos="fade-up">
              <h1>Sobre a MR Centrifugas</h1>
              <p>
                Especialistas em manutenção e fabricação de peças para centrifugas 
                e decanters industriais desde 2016.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Main Content */}
      <section className="section about-content-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} data-aos="fade-right">
              <div className="about-content">
                <h2>Nossa História</h2>
                <p>
                  A MR Centrifugas iniciou suas atividades em 2016, direcionada pelo nosso CEO, 
                  Marcelo Rezende, que possui mais de 25 anos de experiência no ramo industrial. 
                  Nossa missão é fornecer soluções completas para manutenção e fabricação de peças 
                  para centrifugas e decanters industriais.
                </p>
                <p>
                  Estamos localizados na Zona Sul de São Paulo, na região de Interlagos, 
                  onde realizamos as manutenções solicitadas pelos nossos clientes após uma 
                  visita técnica à organização ou envio de seus equipamentos.
                </p>
                <div className="about-features">
                  <div className="feature-item">
                    <FaCheck className="check-icon" />
                    <span>Manutenções preventivas e corretivas</span>
                  </div>
                  <div className="feature-item">
                    <FaCheck className="check-icon" />
                    <span>Comercialização de peças para reposição</span>
                  </div>
                  <div className="feature-item">
                    <FaCheck className="check-icon" />
                    <span>Atendimento em diversos estados do Brasil</span>
                  </div>
                  <div className="feature-item">
                    <FaCheck className="check-icon" />
                    <span>Especialização em Chibrascenter e Pieralisi</span>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6} data-aos="fade-left">
              <div className="about-image">
                <img 
                  src="/about-image.jpg" 
                  alt="Nossa Empresa" 
                  className="img-fluid rounded"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/about-placeholder.jpg';
                  }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Stats Section */}
      <section className="section stats-section">
        <Container>
          <Row>
            {stats.map((stat, index) => (
              <Col lg={3} md={6} key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                <div className="stat-card">
                  <div className="stat-icon">
                    {stat.icon}
                  </div>
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Values Section */}
      <section className="section values-section">
        <Container>
          <div className="section-title" data-aos="fade-up">
            <h2>Nossos Valores</h2>
            <p>Princípios que guiam nosso trabalho e relacionamento com clientes</p>
          </div>
          
          <Row>
            {values.map((value, index) => (
              <Col lg={4} md={6} key={index} data-aos="fade-up" data-aos-delay={index * 100}>
                <Card className="value-card">
                  <Card.Body>
                    <div className="value-icon">
                      {value.icon}
                    </div>
                    <Card.Title>{value.title}</Card.Title>
                    <Card.Text>{value.description}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Team Section */}
      <section className="section team-section">
        <Container>
          <div className="section-title" data-aos="fade-up">
            <h2>Nossa Equipe</h2>
            <p>Profissionais experientes e dedicados ao seu sucesso</p>
          </div>
          
          <Row className="justify-content-center">
            <Col lg={8} data-aos="fade-up">
              <div className="team-member">
                <div className="member-image">
                  <img 
                    src="/marcelo-rezende.jpg" 
                    alt="Marcelo Rezende - CEO" 
                    className="img-fluid rounded-circle"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/member-placeholder.jpg';
                    }}
                  />
                </div>
                <div className="member-info">
                  <h3>Marcelo Rezende</h3>
                  <h4>CEO & Fundador</h4>
                  <p>
                    Com mais de 25 anos de experiência no ramo industrial, Marcelo Rezende 
                    é responsável por liderar a MR Centrifugas e garantir a excelência em 
                    todos os nossos serviços. Sua vasta experiência e conhecimento técnico 
                    são fundamentais para o sucesso da empresa.
                  </p>
                  <div className="member-stats">
                    <div className="stat">
                      <strong>25+</strong>
                      <span>Anos de Experiência</span>
                    </div>
                    <div className="stat">
                      <strong>500+</strong>
                      <span>Projetos Realizados</span>
                    </div>
                    <div className="stat">
                      <strong>50+</strong>
                      <span>Estados Atendidos</span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission Section */}
      <section className="section mission-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6} data-aos="fade-right">
              <div className="mission-content">
                <h2>Nossa Missão</h2>
                <p>
                  Fornecer soluções completas e inovadoras para manutenção e fabricação 
                  de peças para centrifugas e decanters industriais, garantindo a máxima 
                  eficiência e durabilidade dos equipamentos de nossos clientes.
                </p>
                <h3>Nossa Visão</h3>
                <p>
                  Ser referência nacional em soluções para centrifugas industriais, 
                  reconhecida pela qualidade, inovação e compromisso com o cliente.
                </p>
              </div>
            </Col>
            <Col lg={6} data-aos="fade-left">
              <div className="mission-image">
                <img 
                  src="/mission-image.jpg" 
                  alt="Nossa Missão" 
                  className="img-fluid rounded"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '/mission-placeholder.jpg';
                  }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default About; 